import { classToPlain, Exclude, Expose, plainToClass, Transform } from "class-transformer";
import * as t from "io-ts";
import { validateJSON, validateJSONNow } from "../web/validation";
import { Entity } from "./entity";
import { FreightTypeEntity } from "./freight-type";
import { ServiceTypeEntity } from "./service-type";
import { WellStageEntity } from "./well-stage";
import { clone } from "lodash";
import { Exportable } from "./exportable";
import { getDatabaseCodeTransformer } from "./transfomers/transformers";
import { SiteType, toSiteType } from "./site-type";

export const ServiceTypeRuleValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    serviceTypeId: t.string,
    freightTypeId: t.string,
  }),
  t.partial({
    serviceTypeName: t.string,
    freightTypeName: t.string,
    pickupWellStageId: t.string,
    pickupWellStageName: t.string,
    pickupSiteType: t.string,
    deliveryWellStageId: t.string,
    deliveryWellStageName: t.string,
    deliverySiteType: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ServiceTypeRuleArrayValidator = t.array(ServiceTypeRuleValidator);

export type ServiceTypeRule = t.TypeOf<typeof ServiceTypeRuleValidator>;
export type ServiceTypeRuleArray = t.TypeOf<
  typeof ServiceTypeRuleArrayValidator
>;

@Exclude()
export class ServiceTypeRuleEntity implements Entity, Exportable {
  @Expose() id!: string;
  @Expose() regionId!: string;
  @Expose() serviceTypeId!: string;
  @Expose() serviceTypeName?: string;
  @Expose() freightTypeId!: string;
  @Expose() freightTypeName?: string;
  @Expose() pickupWellStageId?: string;
  @Expose() pickupWellStageName?: string;
  @Expose() deliveryWellStageId?: string;
  @Expose() deliveryWellStageName?: string;

  @Transform(getDatabaseCodeTransformer(toSiteType))
  @Expose()
  pickupSiteType?: SiteType;

  @Transform(getDatabaseCodeTransformer(toSiteType))
  @Expose()
  deliverySiteType?: SiteType;

  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'SERVICETYPERULE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for ServiceTypeRuleEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(rule: ServiceTypeRule): ServiceTypeRuleEntity {
    return plainToClass(ServiceTypeRuleEntity, rule, {
      excludeExtraneousValues: true,
    });
  }

  /**
   * Deserializes the ServiceTypeRuleEntity to a ServiceTypeRule type value.
   * If validation fails, the promise will be rejected.
   */
  toValidatedTypeValue(): Promise<ServiceTypeRule> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ServiceTypeRuleValidator, object);
  }

  /**
   * Deserializes the ServiceTypeRuleEntity to a ServiceTypeRule type value.
   */
  toTypeValue(): ServiceTypeRule {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ServiceTypeRuleValidator, object);
  }

  clone(): ServiceTypeRuleEntity {
    return clone(this);
  }

  setServiceType(value: string | ServiceTypeEntity) {
    if (typeof value === 'string') {
      this.serviceTypeId = value;
      this.serviceTypeName = undefined;
    } else {
      this.serviceTypeId = value.id;
      this.serviceTypeName = value.name;
    }
  }

  setFreightType(value: string | FreightTypeEntity) {
    if (typeof value === 'string') {
      this.freightTypeId = value;
      this.freightTypeName = undefined;
    } else {
      this.freightTypeId = value.id;
      this.freightTypeName = value.name;
    }
  }

  setPickupWellStage(value: string | WellStageEntity | undefined) {
    if (typeof value === 'string') {
      this.pickupWellStageId = value;
      this.pickupWellStageName = undefined;
    } else {
      this.pickupWellStageId = value?.id;
      this.pickupWellStageName = value?.stage;
    }
  }

  setDeliveryWellStage(value: string | WellStageEntity | undefined) {
    if (typeof value === 'string') {
      this.deliveryWellStageId = value;
      this.deliveryWellStageName = undefined;
    } else {
      this.deliveryWellStageId = value?.id;
      this.deliveryWellStageName = value?.stage;
    }
  }

  readonly CSV_HEADERS: string[] = [
    'Service Type',
    'Freight Type',
    'Pickup Well Stage',
    'Pickup Site Type',
    'Delivery Well Stage',
    'Delivery Site Type',
  ];

  asCsv(): string[] {
    return [
      this.serviceTypeName ?? '',
      this.freightTypeName ?? '',
      this.pickupWellStageName ?? '',
      this.pickupSiteType?.code ?? '',
      this.deliveryWellStageName ?? '',
      this.deliverySiteType?.code ?? '',
    ];
  }
}
